import type { TaskChangeFile } from "@roomate/core";

/** One rendered line of a unified diff. Hunk lines carry the `@@` header; the others carry old/new line numbers. */
export type DiffLine = { kind: "hunk" | "add" | "del" | "ctx"; text: string; oldNo?: number; newNo?: number };

export type FileDiff = {
  lines: DiffLine[];
  /** The node cut this file's patch at its per-file cap. */
  clipped: boolean;
};

/** Marker the node appends to a file diff it clipped (apps/server/src/agent/task-changes.ts). */
const CLIPPED = /^… \(file diff truncated\)$/;

/** Parse one `diff --git` chunk into hunk/add/del/context lines with line numbers. */
export function parseFileDiff(chunk: string): FileDiff {
  const lines: DiffLine[] = [];
  let clipped = false;
  let oldNo = 0;
  let newNo = 0;
  let inHunk = false;
  for (const raw of chunk.split("\n")) {
    if (CLIPPED.test(raw)) {
      clipped = true;
      continue;
    }
    const hunk = /^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/.exec(raw);
    if (hunk) {
      oldNo = Number(hunk[1]);
      newNo = Number(hunk[2]);
      inHunk = true;
      lines.push({ kind: "hunk", text: raw });
      continue;
    }
    if (!inHunk || raw.startsWith("\\")) continue;
    if (raw.startsWith("+")) lines.push({ kind: "add", text: raw.slice(1), newNo: newNo++ });
    else if (raw.startsWith("-")) lines.push({ kind: "del", text: raw.slice(1), oldNo: oldNo++ });
    else if (raw.startsWith(" ")) lines.push({ kind: "ctx", text: raw.slice(1), oldNo: oldNo++, newNo: newNo++ });
  }
  return { lines, clipped };
}

/**
 * Split a whole `git diff` patch per file and key it by the new path. A file's chunk is found by the
 * `b/<path>` end of its `diff --git` header, so paths with spaces still match.
 */
export function splitPatch(patch: string, files: readonly Pick<TaskChangeFile, "path">[]): Map<string, FileDiff> {
  const out = new Map<string, FileDiff>();
  if (!patch) return out;
  const byLongest = [...files].sort((a, b) => b.path.length - a.path.length);
  for (const chunk of patch.split(/^(?=diff --git )/m)) {
    if (!chunk.startsWith("diff --git ")) continue;
    const header = chunk.slice(0, chunk.indexOf("\n") === -1 ? undefined : chunk.indexOf("\n"));
    const file = byLongest.find((f) => header.endsWith(` b/${f.path}`));
    if (file && !out.has(file.path)) out.set(file.path, parseFileDiff(chunk));
  }
  return out;
}

export type SplitRow = { left?: DiffLine; right?: DiffLine; hunk?: DiffLine };

/** Side-by-side rows: a run of deletions is paired with the run of additions that follows it. */
export function toSplitRows(lines: readonly DiffLine[]): SplitRow[] {
  const rows: SplitRow[] = [];
  for (let i = 0; i < lines.length; ) {
    const line = lines[i]!;
    if (line.kind === "hunk") {
      rows.push({ hunk: line });
      i++;
    } else if (line.kind === "ctx") {
      rows.push({ left: line, right: line });
      i++;
    } else {
      const dels: DiffLine[] = [];
      const adds: DiffLine[] = [];
      while (lines[i]?.kind === "del") dels.push(lines[i++]!);
      while (lines[i]?.kind === "add") adds.push(lines[i++]!);
      for (let k = 0; k < Math.max(dels.length, adds.length); k++) rows.push({ left: dels[k], right: adds[k] });
    }
  }
  return rows;
}

export const STATUS_LABEL: Record<TaskChangeFile["status"], string> = { A: "added", M: "modified", D: "deleted", R: "renamed" };

/** Filled cells (of 5) of the +/- bar for a file: proportional split of its insertions and deletions. */
export function changeBar(insertions: number, deletions: number, cells = 5): { add: number; del: number; rest: number } {
  const total = insertions + deletions;
  if (total === 0) return { add: 0, del: 0, rest: cells };
  let add = Math.round((insertions / total) * cells);
  let del = cells - add;
  if (insertions > 0 && add === 0) (add = 1), (del = cells - 1);
  if (deletions > 0 && del === 0) (del = 1), (add = cells - 1);
  return { add, del, rest: 0 };
}
